import {
  Button,
  Dialog,
  DialogContent,
  FormControl,
  FormHelperText,
  Grid,
  Divider,
  InputLabel,
  Link,
  TextField,
  Typography,
  withStyles,
  Select,
  Theme,
  createStyles,
  WithStyles,
} from '@material-ui/core';
import { isValidPhoneNumber } from 'react-phone-number-input';
import React, { Fragment, useState, useEffect, ChangeEvent } from 'react';
import { withLocale } from '../../../../../hocs/withLocale';
import { compose } from 'recompose';
import { serviceLocator } from '../../../../../infrastructure/ServiceLocator';
import LayoutHelper from '../../../../../infrastructure/LayoutHelper';
import { withFormik, FormikProps, FormikBag } from 'formik';
import { BookingTermsAndConditions } from '../../../BookingTermsAndConditions';
import SnackbarContent from '@material-ui/core/SnackbarContent';
import { Recaptcha } from '../../../../../components/Shared/Recaptcha';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import MuiPhoneInput from '../../../../../components/Form/MuiPhoneInput';
import CircularProgress from '@material-ui/core/CircularProgress';
import { calculatePriceAfterDiscount } from '../../../../../utils/price';
import {
  LIQ_PAY_PAYMENT_SOURCE,
  MONOBANK_PAYMENT_SOURCE,
  STRIPE_PAYMENT_SOURCE,
  WAY_FOR_PAY_PAYMENT_SOURCE,
} from '../../../../../constants/paymentConstants';
import moment from 'moment';

interface FormFieldProps extends WithStyles<typeof formFieldStyles> {
  label: React.ReactNode;
  name: string;
  errors: Record<string, string>;
  children: React.ReactNode;
}

const formFieldStyles = (theme: Theme) =>
  createStyles({
    field: {
      display: 'flex',
      flexDirection: 'column',
      margin: `${theme.spacing(2)}px 0`,
    },
    label: {
      marginBottom: theme.spacing(1),
      position: 'relative',
      transform: 'none',
      display: 'flex',
      alignItems: 'center',
    },
  });

const FormField: React.FC<FormFieldProps> = ({ label, name, errors, children, classes }) => {
  const error = errors[name];
  return (
    <FormControl className={classes.field} error={!!error}>
      <InputLabel className={classes.label}>
        {label}
        {error && <FormHelperText>&nbsp;{error}</FormHelperText>}
      </InputLabel>
      {children}
    </FormControl>
  );
};

const StyledFormField = withStyles(formFieldStyles)(FormField);

interface Discount {
  ratePlanId: string;
  discount: number;
  discountType: string;
}

interface StudioClient {
  _id: string;
  name?: string;
  email?: string;
  phone?: string;
  discounts?: Discount[];
  new?: boolean;
  [key: string]: any;
}

interface RatePlan {
  _id: string;
  name: string;
  price: number;
  billingTags?: string[];
  studioIds?: string[];
  periodNumber: number;
  period: string;
  permanent?: boolean;
  [key: string]: any;
}

interface Owner {
  _id: string;
  name: string;
  liqPayPayment?: any;
  wayForPayPayment?: any;
  organizationSettings?: {
    stripeConfigs?: {
      apiKey?: string;
    };
  };
  [key: string]: any;
}

interface FormValues {
  domain: {
    id: string;
    countryCode: string;
    currencyCode: string;
  };
  date: moment.Moment;
  validTill: moment.Moment;
  dateStr: string;
  studioClient?: StudioClient;
  clientName?: string;
  clientEmail?: string;
  phone?: string;
  tcAccepted?: boolean;
  captcha?: boolean;
  errors?: string[];
}

interface ClientRatePlanFormProps extends WithSnackbarProps {
  owner: Owner;
  studioClient?: StudioClient;
  studio?: any;
  ratePlan: RatePlan;
  date: moment.Moment;
  translate: (key: string, data?: any) => string;
  classes: any;
  setAttendance?: any;
  onSuccess?: any;
  onFailure?: any;
  course?: any;
  paymentSource?: string;
  domain: {
    id: string;
    countryCode: string;
    currencyCode: string;
  };
}

interface UnpaidClasses {
  start: string;
  number: number;
}

interface PendingClient {
  _id: string;
  [key: string]: any;
}

const ClientRatePlanFormComponent: React.FC<ClientRatePlanFormProps & FormikProps<FormValues>> = ({
  owner,
  studioClient,
  studio,
  ratePlan,
  setAttendance,
  onSuccess,
  onFailure,
  values,
  errors,
  handleChange,
  isSubmitting,
  setSubmitting,
  translate,
  classes,
  enqueueSnackbar,
  dirty,
  course,
  isValid,
  paymentSource,
}) => {
  const [validatingClient, setValidatingClient] = useState<boolean>(false);
  const [loadingPendingClient, setLoadingPendingClient] = useState<boolean>(false);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [newPendingClient, setNewPendingClient] = useState<PendingClient | undefined>(undefined);
  const [unpaidClasses, setUnpaidClasses] = useState<UnpaidClasses | undefined>(undefined);
  const [linkToPay, setLinkToPay] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string | null>(null);
  const availablePaymentMethods = getAvailablePaymentMethods(owner);

  const discount = studioClient?.discounts?.find((d) => d.ratePlanId === ratePlan._id);
  let amount = ratePlan.price;
  if (discount) {
    amount = calculatePriceAfterDiscount(ratePlan.price, discount.discount, discount.discountType);
  }

  const getClient = async (phone: string): Promise<void> => {
    if (!(phone && isValidPhoneNumber(phone))) return;
    setValidatingClient(true);
    const api = serviceLocator.clientApi;
    const data = await api.checkPhone(owner._id, phone, undefined);
    const clients = data.clients;

    if (clients && clients.length > 0) {
      const client = clients[0];
      handleChange('studioClient')(client);
      handleChange('clientName')(client.name);
      handleChange('clientEmail')(client.email);
      findFirstUnpaidClass(client._id);
    }
    setValidatingClient(false);
  };

  function getPaymentLinkWithQuery(): string {
    const paramsForQuery: Record<string, string> = {
      date: values.dateStr,
      validTill: values.validTill.format('YYYY-MM-DD'),
      studioClientId: values.studioClient?._id,
      phone: values.phone || '',
      email: values.clientEmail || '',
      pendingClientId: newPendingClient?._id,
    };
    const queryParams = new URLSearchParams(
      Object.entries(paramsForQuery).filter(([key, value]) => value !== undefined) as [string, string][]
    );
    return `${linkToPay}&${queryParams.toString()}`;
  }

  function getAvailablePaymentMethods(owner: Owner): string[] {
    const availableMethods: string[] = [];
    if (owner.organizationSettings?.stripeConfigs?.apiKey) {
      availableMethods.push(STRIPE_PAYMENT_SOURCE);
    }
    if (owner.liqPayPayment) {
      availableMethods.push(LIQ_PAY_PAYMENT_SOURCE);
    }
    if (owner.wayForPayPayment) {
      availableMethods.push(WAY_FOR_PAY_PAYMENT_SOURCE);
    }
    if (owner.monobankAcquiringPayment) {
      availableMethods.push(MONOBANK_PAYMENT_SOURCE);
    }
    return availableMethods;
  }

  function getPaymentLink(paymentSource: string, owner: Owner, ratePlan: RatePlan): string {
    switch (paymentSource) {
      case STRIPE_PAYMENT_SOURCE:
        return `/stripe-rate-plan/${owner._id}/${ratePlan._id}?amount=${amount}&purpose=RATE_PLAN&subPurpose=${ratePlan.name}`;

      case LIQ_PAY_PAYMENT_SOURCE:
        return `/liqPay-rate-plan/${owner._id}/${ratePlan._id}?amount=${amount}&purpose=RATE_PLAN&subPurpose=${ratePlan.name}`;

      case WAY_FOR_PAY_PAYMENT_SOURCE:
        return `/wayForPay-rate-plan/${owner._id}/${ratePlan._id}?amount=${amount}&purpose=RATE_PLAN&subPurpose=${ratePlan.name}`;

      case MONOBANK_PAYMENT_SOURCE:
        return `/monobank-rate-plan/${owner._id}/${ratePlan._id}?amount=${amount}&purpose=RATE_PLAN&subPurpose=${ratePlan.name}`;

      default:
        return '';
    }
  }

  const handleEmailChange = (e: ChangeEvent<HTMLInputElement>): void => {
    let value = e.target.value;
    if (value) {
      value = value.trim();
    }
    handleChange('clientEmail')(value);
  };

  const findFirstUnpaidClass = async (studioClientId: string): Promise<void> => {
    const api = serviceLocator.registrationApi;
    const data = await api.findFirstUnpaidClass(owner._id, studioClientId, ratePlan.billingTags, ratePlan.studioIds);

    if (data.unpaidClasses && data.unpaidClasses.length > 0) {
      const firstClass = data.unpaidClasses[0];
      const validFrom = moment.utc(firstClass.classDateTime).startOf('day');
      handleChange('dateStr')(validFrom.format('YYYY-MM-DD'));
      handleChange('date')(validFrom);
      setUnpaidClasses({
        start: validFrom.format('DD MMM YYYY'),
        number: data.unpaidClasses.length,
      });
    }
  };

  useEffect(() => {
    if (values.studioClient) {
      findFirstUnpaidClass(values.studioClient._id);
    }
    if (!paymentSource) {
      if (availablePaymentMethods.length) {
        setSelectedSource(availablePaymentMethods[0]);
        setLinkToPay(getPaymentLink(availablePaymentMethods[0], owner, ratePlan));
      }
    } else {
      setLinkToPay(getPaymentLink(paymentSource, owner, ratePlan));
    }
  }, []);

  if (values.errors) {
    return (
      <>
        {values.errors.map((e, idx) => (
          <SnackbarContent key={idx} message={e} />
        ))}
      </>
    );
  }

  return (
    <div>
      <Grid container direction="column">
        {unpaidClasses && <SnackbarContent message={translate('myClients.unpaidClasses', unpaidClasses)} />}
        {!paymentSource && availablePaymentMethods.length !== 1 && (
          <div>
            <Typography variant="h6">{translate('myClients.paymentSource')}</Typography>
            <Select
              native
              fullWidth
              variant="outlined"
              value={selectedSource}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                setSelectedSource(event.target.value as string);
                setLinkToPay(getPaymentLink(event.target.value as string, owner, ratePlan));
              }}
            >
              {availablePaymentMethods.map((paymentMethod) => {
                switch (paymentMethod) {
                  case LIQ_PAY_PAYMENT_SOURCE:
                    return (
                      <option key={LIQ_PAY_PAYMENT_SOURCE} value={LIQ_PAY_PAYMENT_SOURCE}>
                        LiqPay
                      </option>
                    );
                  case STRIPE_PAYMENT_SOURCE:
                    return (
                      <option key={STRIPE_PAYMENT_SOURCE} value={STRIPE_PAYMENT_SOURCE}>
                        Stripe
                      </option>
                    );
                  case WAY_FOR_PAY_PAYMENT_SOURCE:
                    return (
                      <option key={WAY_FOR_PAY_PAYMENT_SOURCE} value={WAY_FOR_PAY_PAYMENT_SOURCE}>
                        WayForPay
                      </option>
                    );
                  default:
                    return null;
                }
              })}
            </Select>
          </div>
        )}
        <StyledFormField
          label={<Fragment>{translate('manageRegistration.ratePlanValidFrom')}</Fragment>}
          errors={errors}
          name="dateStr"
        >
          <TextField
            type="date"
            fullWidth
            helperText={
              !ratePlan.permanent && (
                <Fragment>
                  {translate('manageRegistration.ratePlanValidTill')} {values.validTill.format('DD MMM YYYY')}
                </Fragment>
              )
            }
            variant="outlined"
            value={values.dateStr}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value;
              const date = moment.utc(value, 'YYYY-MM-DD');
              handleChange('dateStr')(value);
              if (date.isValid()) {
                handleChange('date')(date);
                const validTill = moment.utc(date).add(ratePlan.periodNumber, ratePlan.period).add(-1, 'days');
                handleChange('validTill')(validTill);
              } else {
                handleChange('date')(null);
                handleChange('validTill')(null);
              }
            }}
          />
        </StyledFormField>

        <Typography align="center" variant="h6">
          {translate('myClients.clientInfo')}
        </Typography>
        <Divider />
        <StyledFormField label={<b>{translate('manageRegistration.clientPhone')}*</b>} errors={errors} name="phone">
          <MuiPhoneInput
            variant="outlined"
            className={classes.phoneInput}
            defaultCountry={values.domain.countryCode}
            value={values.phone}
            id="phone"
            onChange={(phone: string) => {
              handleChange('phone')(phone);
              if (!studioClient) {
                setNewPendingClient(undefined);
                handleChange('studioClient')(undefined);
                handleChange('clientName')('');
                handleChange('clientEmail')('');
              }
              return getClient(phone);
            }}
          />
        </StyledFormField>
        {validatingClient && (
          <Typography>
            <CircularProgress style={{ width: '16px', height: '16px' }} /> Validating your phone
          </Typography>
        )}
        <StyledFormField label={<b>{translate('manageRegistration.clientName')}</b>} errors={errors} name="clientName">
          <TextField
            variant="outlined"
            id="clientName"
            value={values.clientName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              handleChange('clientName')(e.target.value);
            }}
            margin="normal"
          />
        </StyledFormField>
        <StyledFormField label={translate('manageRegistration.clientEmail')} errors={errors} name="clientEmail">
          <TextField
            id="clientEmail"
            value={values.clientEmail}
            variant="outlined"
            onChange={handleEmailChange}
            margin="normal"
          />
        </StyledFormField>

        {!values.studioClient && (
          <Fragment>
            <BookingTermsAndConditions
              accepted={values.tcAccepted}
              onChange={(a: boolean) => {
                handleChange('tcAccepted')(a);
              }}
              owner={owner}
            />
            <div style={{ marginBottom: '10px' }}>
              <Recaptcha onVerify={(canSubmit: boolean) => handleChange('captcha')(canSubmit)} />
            </div>
          </Fragment>
        )}

        <div className={classes.buttons}>
          {values.studioClient ? (
            <Button
              disabled={errors && Object.keys(errors).length > 0}
              fullWidth
              style={{
                padding: '20px',
                fontWeight: 900,
                color: '#fff',
                background: errors && Object.keys(errors).length > 0 ? 'lightgray' : 'rgba(122,183,43,1)',
              }}
              component={Link}
              href={getPaymentLinkWithQuery()}
            >
              {translate('studio.class.pay')}({amount}
              {values.domain.currencyCode})
            </Button>
          ) : (
            <Button
              disabled={errors && Object.keys(errors).length > 0}
              onClick={async () => {
                setLoadingPendingClient(true);
                const pendingClient = await serviceLocator.onlineRegistrationsApiCall.addPendingClient({
                  client: {
                    clientName: values.clientName,
                    phone: values.phone,
                    email: values.clientEmail,
                  },
                  ownerId: owner._id,
                });
                setNewPendingClient(pendingClient);
                setShowDialog(true);
                setLoadingPendingClient(false);
              }}
              fullWidth
              style={{
                padding: '20px',
                fontWeight: 900,
                color: '#fff',
                background: errors && Object.keys(errors).length > 0 ? 'lightgray' : 'rgba(122,183,43,1)',
              }}
            >
              {translate('studio.class.pay')}({amount}
              {values.domain.currencyCode})
            </Button>
          )}
        </div>
      </Grid>
      {newPendingClient && (
        <Dialog
          onClose={() => {
            setShowDialog(false);
          }}
          open={showDialog}
        >
          <DialogContent>
            {loadingPendingClient ? (
              <CircularProgress />
            ) : (
              <Grid container spacing={1}>
                <Fragment>
                  <Grid item xs={12}>
                    <Typography variant="h6" align="center">
                      {translate('manageRegistration.buyRatePlanAsNewCustomer')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Button
                      fullWidth
                      style={{
                        padding: '20px',
                        fontWeight: 900,
                        color: '#fff',
                        background: 'rgba(122,183,43,1)',
                      }}
                      component={Link}
                      href={getPaymentLinkWithQuery()}
                    >
                      {translate('studio.class.pay')}({amount}
                      {values.domain.currencyCode})
                    </Button>
                  </Grid>
                </Fragment>
              </Grid>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

const styles = (theme: Theme) =>
  createStyles({
    phoneInput: {
      marginTop: theme.spacing(1.5),
    },
    containerWidth: { width: 'auto' },
    confirmed: {
      height: theme.spacing(10),
      color: 'green',
      display: 'flex',
      alignItems: 'center',
    },
    buttons: {
      [theme.breakpoints.down('xs')]: {
        position: 'fixed',
        left: 0,
        bottom: 0,
        width: '100%',
      },
    },
  });

export const ClientRatePlanForm = compose(
  withLocale,
  withSnackbar,
  withStyles(styles),
  withFormik<ClientRatePlanFormProps, FormValues>({
    mapPropsToValues: (props) => ({
      domain: props.domain,
      date: props.date,
      validTill: moment.utc(props.date).add(props.ratePlan.periodNumber, props.ratePlan.period).add(-1, 'days'),
      dateStr: props.date.format('YYYY-MM-DD'),
      studioClient: props.studioClient,
      clientName: props.studioClient && props.studioClient.name,
      clientEmail: props.studioClient && props.studioClient.email,
      phone: props.studioClient && props.studioClient.phone,
    }),
    validate: (values, props) => {
      const errors: Record<string, string> = {};

      if (values.clientEmail && !LayoutHelper.isEmailValid(values.clientEmail)) {
        errors.clientEmail = props.translate('common.invalidEmail');
      }
      if (values.phone && !isValidPhoneNumber(values.phone)) {
        errors.phone = props.translate('common.invalidPhone');
      }

      const mandatoryFields = ['dateStr', 'clientName'];
      if (!props.studioClient) {
        mandatoryFields.push('phone');
      }
      if (!values.studioClient) {
        mandatoryFields.push('tcAccepted');
        mandatoryFields.push('captcha');
      }
      mandatoryFields.forEach((field) => {
        if (!values[field as keyof FormValues]) {
          errors[field] = '*';
        }
      });
      return errors;
    },
    displayName: 'BookingClassInfoForm',
  })
)(ClientRatePlanFormComponent);
